import React, { useLayoutEffect, useState } from "react";
import Modal from "react-bootstrap/Modal";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import AccountService from "../../Services/Account/AccountService";
import { setWebInfo } from "../../Redux/Actions/Index";
import { cacheAuthenticatorEnabled } from "./authenticatorStatusCache";
import Commonservice from "Services/CommonService";
import LoadButton from "Shared/Common/LoadButton";
import useAuth from "Shared/hooks/useAuth";
import useLang from "Shared/hooks/useLanguage";
import {
  setFaviconAndTitle,
  setValueIntoSessionStorage,
} from "Utils/Common/CommonMethods";

const PAGE_ID = 18;

type DisableStep = "confirm" | "password" | "success";

const pageShellStyle = {
  backgroundImage: 'url("/media/illustrations/login/sketch-1.png")',
} as const;

const DisableAuthenticator: React.FC = () => {
  const { t } = useLang();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { navigateToLandingPage } = useAuth();

  const [step, setStep] = useState<DisableStep>("confirm");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);

  const syncWebInfo = () => {
    Commonservice.getLoginPageLogo()
      .then((res: any) => {
        const data = res?.data;
        if (data && typeof data !== "string") {
          dispatch(
            setWebInfo({
              smartLogoUrl: data?.smartLogo,
              title: data?.labName,
            })
          );
          setFaviconAndTitle(data?.smartLogo, data?.labName);
        }
      })
      .catch((err: any) => {
        console.trace(err);
      });
  };

  const goToSettings = () => navigate("/authenticator-settings");

  const handleConfirmRemoval = async () => {
    if (!password.trim()) {
      toast.error(t("Password is required"));
      return;
    }

    try {
      setSubmitting(true);
      const res = await AccountService.confirmAuthenticatorRemoval(password);
      const payload = res?.data;
      if (
        payload?.succeeded ||
        payload?.isSuccess ||
        payload?.statusCode === 200
      ) {
        cacheAuthenticatorEnabled(false);
        setStep("success");
      } else {
        toast.error(payload?.message || t("Invalid password"));
      }
    } catch (err: any) {
      console.trace(err);
      toast.error(
        err?.response?.data?.message || t("Failed to verify password")
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelAnyway = () => {
    setShowCancelModal(false);
    goToSettings();
  };

  useLayoutEffect(() => {
    setValueIntoSessionStorage("pageId", PAGE_ID);
    syncWebInfo();
  }, []);

  const renderConfirmStep = () => (
    <div
      className="w-lg-400px bg-body rounded shadow-sm px-8 py-6 mx-auto d-flex flex-column"
      style={{ maxWidth: 400, minHeight: 321 }}
    >
      <div className="text-center">
        <h3 className="mb-0">{t("Disable Authenticator")}</h3>
      </div>
      <div className="text-center d-flex flex-grow-1 align-items-center justify-content-center">
        <h2
          className="fw-semibold mb-0 text-center"
          style={{ color: "#000000" }}
        >
          {t("Do you really want to remove Authenticator?")}
        </h2>
      </div>
      <div className="d-flex justify-content-center align-items-center gap-4">
        <button
          type="button"
          className="btn btn-primary h-45px"
          style={{ width: "max-content", whiteSpace: "nowrap" }}
          onClick={() => {
            setPassword("");
            setShowPassword(false);
            setStep("password");
          }}
          data-test-id="disable-authenticator-continue"
        >
          {t("Continue")}
        </button>
        <button
          type="button"
          className="btn btn-secondary h-45px"
          style={{ width: "max-content", whiteSpace: "nowrap" }}
          onClick={goToSettings}
          data-test-id="disable-authenticator-cancel"
        >
          {t("Cancel")}
        </button>
      </div>
    </div>
  );

  const renderPasswordStep = () => (
    <div className="w-lg-600px bg-body rounded shadow-sm p-10 p-lg-15 mx-auto">
      <div className="text-center mb-8">
        <h3 className="mb-3">{t("Password Verification")}</h3>
        <p className="text-gray-700 mb-0">
          {t("Please enter your password to remove authenticator")}
        </p>
      </div>
      <div className="fv-row mb-10">
        <div className="d-flex">
          <input
            type={showPassword ? "text" : "password"}
            className="form-control form-control-lg"
            placeholder={t("Password")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-test-id="disable-authenticator-password"
            onKeyDown={(e) => {
              if (e.key === "Enter") handleConfirmRemoval();
            }}
          />
          <button
            type="button"
            className="btn btn-outline-primary"
            onClick={() => setShowPassword((prev) => !prev)}
            data-test-id="toggle-disable-authenticator-password"
          >
            <i className={`bi ${showPassword ? "bi-eye-slash" : "bi-eye"}`}></i>
          </button>
        </div>
      </div>
      <div className="d-flex justify-content-center align-items-center gap-4">
        <LoadButton
          id="disable-authenticator-password-submit"
          className="btn btn-primary h-45px"
          style={{ width: "max-content", whiteSpace: "nowrap" }}
          loading={submitting}
          btnText={t("Submit")}
          loadingText={t("Verifying")}
          onClick={handleConfirmRemoval}
          disabled={!password.trim()}
        />
        <button
          type="button"
          className="btn btn-secondary h-45px"
          style={{ width: "max-content", whiteSpace: "nowrap" }}
          onClick={() => setShowCancelModal(true)}
          data-test-id="disable-authenticator-password-cancel"
        >
          {t("Cancel")}
        </button>
      </div>
    </div>
  );

  const renderSuccessStep = () => (
    <div className="w-lg-600px bg-body rounded shadow-sm p-10 p-lg-15 mx-auto text-center">
      <div className="mb-6">
        <i
          className="fa-regular fa-circle-check text-success"
          style={{ fontSize: "8.5rem" }}
          aria-hidden
        />
      </div>
      <p className="text-gray-800 fs-4 mb-10">
        {t("Authenticator is successfully removed from your account")}
      </p>
      <button
        type="button"
        className="btn btn-primary h-45px"
        style={{ width: "max-content", whiteSpace: "nowrap" }}
        onClick={() => navigateToLandingPage()}
        data-test-id="authenticator-success-done"
      >
        {t("Done")}
      </button>
    </div>
  );

  return (
    <div
      className="d-flex flex-column flex-column-fluid bgi-position-y-bottom position-x-center bgi-no-repeat bgi-size-contain bgi-attachment-fixed"
      style={pageShellStyle}
    >
      <div
        className="d-flex flex-center flex-column flex-column-fluid p-10 pb-lg-20"
        style={{ minHeight: "100vh" }}
      >
        {step === "confirm" && renderConfirmStep()}
        {step === "password" && renderPasswordStep()}
        {step === "success" && renderSuccessStep()}
      </div>

      <Modal
        show={showCancelModal}
        onHide={() => setShowCancelModal(false)}
        backdrop="static"
      >
        <Modal.Body className="text-center p-8">
          <p className="fs-5 text-gray-800 mb-8">
            {t(
              "Authenticator removal is not complete. Are you sure you want to cancel?"
            )}
          </p>
          <div className="d-flex justify-content-center align-items-center gap-4 flex-wrap">
            <button
              type="button"
              className="btn btn-danger h-45px"
              style={{ width: "max-content", whiteSpace: "nowrap" }}
              onClick={handleCancelAnyway}
              data-test-id="cancel-authenticator-removal-anyway"
            >
              {t("Cancel anyway")}
            </button>
            <button
              type="button"
              className="btn btn-primary h-45px"
              style={{ width: "max-content", whiteSpace: "nowrap" }}
              onClick={() => setShowCancelModal(false)}
              data-test-id="continue-authenticator-removal"
            >
              {t("Continue")}
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default DisableAuthenticator;
